(() => {
  if (customElements.get('clikea-catalog-pages')) return;
  class CatalogPages extends HTMLElement {
    connectedCallback() {
      const grid = this.closest('.shopify-section').querySelector('#js-results');
      if (!grid) return;
      const retained = this.grid === grid && this.blocks > 1;
      this.grid = grid;
      if (!retained) { this.page = Number(this.dataset.page); this.blocks = 1; }
      this.limit = Math.max(1, Math.min(10, Math.floor(120 / Number(this.dataset.pageSize || 12))));
      if (!retained) this.next = this.dataset.next;
      this.busy = false;
      this.failed = false;
      this.status = this.querySelector('[data-catalog-status]');
      this.link = this.querySelector('[data-catalog-next]');
      this.retry = this.querySelector('[data-catalog-retry]');
      this.retryClick = () => { this.failed = false; this.load(); };
      this.retry.addEventListener('click', this.retryClick);
      this.visibility = () => {
        if (document.hidden) { this.observer?.disconnect(); this.controller?.abort(); }
        else this.observe();
      };
      document.addEventListener('visibilitychange', this.visibility);
      const stableQuery = new URLSearchParams();
      for (const [name, value] of new URL(location.href).searchParams) {
        if (name.startsWith('filter.') || ['page', 'sort_by', 'q', 'type', 'options[prefix]'].includes(name)) stableQuery.append(name, value);
      }
      stableQuery.sort();
      this.key = `clikea-catalog:${location.pathname}?${stableQuery}`;
      this.save = () => {
        try { sessionStorage.setItem(this.key, JSON.stringify({ blocks: this.blocks, scroll: scrollY, at: Date.now() })); } catch {}
      };
      window.addEventListener('pagehide', this.save);
      this.update();
      if (retained) this.observe(); else this.restore();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.retry?.removeEventListener('click', this.retryClick);
      document.removeEventListener('visibilitychange', this.visibility);
      window.removeEventListener('pagehide', this.save);
    }
    async restore() {
      let saved;
      try {
        if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') saved = JSON.parse(sessionStorage.getItem(this.key));
      } catch {}
      if (saved && Date.now() - saved.at < 30 * 60 * 1000 && saved.blocks > 1) {
        this.restoring = true;
        for (let i = 1; i < Math.min(this.limit, saved.blocks); i++) {
          if (!this.next || this.failed || document.hidden || !this.isConnected) break;
          await this.load();
        }
        this.restoring = false;
        if (this.isConnected && !document.hidden) window.scrollTo({ top: saved.scroll, behavior: 'instant' });
      }
      this.observe();
    }
    observe() {
      this.observer?.disconnect();
      if (!this.isConnected || document.hidden || this.suspended || this.restoring || this.busy || this.failed || !this.next || this.blocks >= this.limit) return;
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.load();
      }, { rootMargin: '0px 0px 480px 0px' });
      this.observer.observe(this);
    }
    update() {
      this.setAttribute('aria-busy', String(this.busy));
      this.dataset.loading = String(this.busy);
      this.retry.hidden = !this.failed;
      this.link.hidden = !this.next || this.busy || ('IntersectionObserver' in window && !this.failed && this.blocks < this.limit);
      if (this.next) this.link.href = this.next;
      this.link.textContent = this.blocks >= this.limit ? this.dataset.continue : this.dataset.nextLabel;
      this.status.textContent = this.failed ? this.dataset.error : this.busy ? this.dataset.loadingLabel : !this.next ? this.dataset.end : this.blocks >= this.limit ? this.dataset.limitLabel : this.dataset.ready;
      const progress = this.querySelector('[data-catalog-progress]');
      progress.textContent = `${this.dataset.page}–${this.page} / ${this.dataset.pages}`;
      this.dataset.loadedBlocks = this.blocks;
    }
    async load() {
      if (this.busy || this.suspended || !this.next || this.blocks >= this.limit || document.hidden || !this.isConnected) return;
      const url = new URL(this.next, location.origin);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      this.busy = true;
      this.failed = false;
      this.observer?.disconnect();
      this.controller = new AbortController();
      const controller = this.controller;
      const timeout = setTimeout(() => controller.abort('timeout'), 15000);
      url.searchParams.set('section_id', this.dataset.section);
      this.update();
      try {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const documentNext = new DOMParser().parseFromString(await response.text(), 'text/html');
        const marker = documentNext.querySelector('clikea-catalog-pages');
        const grid = documentNext.querySelector('#js-results');
        if (!marker || !grid || marker.dataset.section !== this.dataset.section || Number(marker.dataset.page) !== this.page + 1) throw new Error('Unexpected page');
        if (!this.isConnected || controller.signal.aborted) return;
        const ids = new Set([...this.grid.querySelectorAll('[data-catalog-product]')].map(node => node.dataset.catalogProduct));
        const fragment = document.createDocumentFragment();
        for (const node of [...grid.children]) {
          if (node.dataset.catalogProduct && ids.has(node.dataset.catalogProduct)) continue;
          if (node.dataset.catalogProduct) ids.add(node.dataset.catalogProduct);
          node.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; img.fetchPriority = 'auto'; });
          fragment.append(document.importNode(node, true));
        }
        this.grid.append(fragment);
        this.page = Number(marker.dataset.page);
        this.blocks++;
        this.next = marker.dataset.next;
      } catch {
        if (this.isConnected && !this.suspended && !document.hidden) this.failed = true;
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        if (this.isConnected) { this.update(); this.observe(); }
      }
    }
  }
  customElements.define('clikea-catalog-pages', CatalogPages);
  document.addEventListener('click', event => {
    const shortcut = event.target.closest('[data-catalog-filter]');
    if (!shortcut) return;
    requestAnimationFrame(() => {
      const dialog = shortcut.closest('form')?.querySelector('[role="dialog"]');
      const buttons = [...(dialog?.querySelectorAll('button') || [])];
      const target = buttons.find(button => button.textContent.trim().toLowerCase().startsWith(shortcut.dataset.catalogFilter));
      if (target && target.getAttribute('aria-expanded') !== 'true') target.click();
    });
  });
})();
